import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import SettingsIcon from 'icons/Settings.icon';
import TablePanelDropdown from 'components/TablePanelDropdown';
import useCallbackRef from 'hooks/useCallbackRef';
import { TablePanelDropdownRef } from 'components/TablePanelDropdown/TablePanelDropdown.component';
import ManageColumnsForm from 'components/ManageColumnsForm';
import TableControlButton from 'components/TableControlButton';
import { getTableHiddenColumns } from 'components/ReorderResizeTable/utils';

import './ManageColumns.styles.scss';

type ManageColumnsProps<T extends string> = {
  className?: string;
  allColumns: T[];
  hiddenColumns: string[];
  headingTranslations: Partial<Record<T, string>>;
  lockedColumn?: T;
  isDefaultColumns: boolean;
  onSave: (hiddenColumns: string[], columnOrder: string[]) => void;
  onResetToDefault: () => void;
};

const ManageColumns = <T extends string>(props: ManageColumnsProps<T>) => {
  const {
    className,
    allColumns,
    hiddenColumns,
    headingTranslations,
    lockedColumn,
    isDefaultColumns,
    onSave,
    onResetToDefault,
  } = props;

  const [dropdown, dropdownRef] = useCallbackRef<TablePanelDropdownRef>();

  const [formKey, setFormKey] = useState(0);

  const classes = classNames('manage-columns', className);

  const { t } = useTranslation();

  const initialValues = useMemo(
    () => ({
      hiddenColumns: getTableHiddenColumns(allColumns, hiddenColumns),
      columnOrder: allColumns,
    }),
    [allColumns, hiddenColumns],
  );

  const handleOnClose = useCallback(() => dropdown?.close(), [dropdown]);

  const remountForm = useCallback(() => setFormKey((key) => key + 1), []);

  const handleOnSave = useCallback(
    (hiddenColumns: string[], columnOrder: string[]) => {
      onSave(hiddenColumns, columnOrder);
      handleOnClose();
    },
    [handleOnClose, onSave],
  );

  const handleRestoreDefault = useCallback(() => {
    onResetToDefault();
    handleOnClose();
  }, [handleOnClose, onResetToDefault]);

  return (
    <TablePanelDropdown
      ref={dropdownRef}
      className={classes}
      title={t('manageColumns')}
      subtitle={t('chooseColumnsSubtitle')}
      onClose={remountForm}
      trigger={
        <div className="table-panel-dropdown__trigger-wrapper">
          <TableControlButton
            icon={SettingsIcon}
            tooltip={t('manageColumns')}
          />
        </div>
      }
    >
      <ManageColumnsForm
        key={formKey}
        initialValues={initialValues}
        headingTranslations={headingTranslations}
        lockedColumn={lockedColumn}
        isDefaultColumns={isDefaultColumns}
        saveLabel={t('save')}
        onSave={handleOnSave}
        onRestore={handleRestoreDefault}
        onCancel={handleOnClose}
      />
    </TablePanelDropdown>
  );
};

export default ManageColumns;
